import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { nuvraAuth } from "@/auth/nuvraAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Mail, Eye, EyeOff } from "lucide-react";
import { NuvraLogo } from "@/components/NuvraLogo";
import { Footer } from "@/components/Footer";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { Separator } from "@/components/ui/separator";
import { useI18n } from "@/i18n/I18nContext";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState<{ challengeId: string; userId: string; method?: string; stepUpReason?: string; enrollmentRequired?: boolean } | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [magicMode, setMagicMode] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useI18n();

  // Honor a return path (e.g. a share-link /join redeem that bounced here when
  // signed out). Only same-origin relative paths are accepted, never an open
  // redirect to another host.
  const nextParam = searchParams.get("next");
  const dest = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/dashboard";

  // Nuvra-owned login (its own /api/auth, device-aware sessions, MFA, audit).
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const result = await nuvraAuth.login({ email, password });
    setLoading(false);
    if (result.mfaRequired) {
      setMfa({
        challengeId: result.mfaChallengeId!,
        userId: result.userId!,
        method: result.mfaMethod,
        stepUpReason: result.stepUpReason,
        enrollmentRequired: result.mfaEnrollmentRequired,
      });
      return;
    }
    if (!result.success) {
      toast({ variant: "destructive", title: t("auth.login.failedTitle"), description: result.error });
      return;
    }
    navigate(dest);
  };

  // Passwordless sign-in: Nuvra emails a one-time link (its own token + Resend).
  // Enumeration-safe, so we always show the confirmation once the request lands.
  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const result = await nuvraAuth.sendMagicLink({ email });
    setLoading(false);
    if (!result.success) {
      toast({ variant: "destructive", title: t("auth.login.failedTitle"), description: result.error });
      return;
    }
    setMagicSent(true);
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfa) return;
    setLoading(true);
    const result = await nuvraAuth.verifyMfa({ userId: mfa.userId, challengeId: mfa.challengeId, code: mfaCode });
    setLoading(false);
    if (!result.success) {
      toast({ variant: "destructive", title: t("auth.login.failedTitle"), description: result.error });
      return;
    }
    navigate(dest);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-primary/10 blur-[120px]" />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md px-4 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("auth.backToHome")}
          </Link>

          <Card className="border-border/50 bg-card/80 backdrop-blur-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <NuvraLogo size={32} />
                <span className="font-bold text-xl text-foreground">Nuvra</span>
              </div>
              <CardTitle className="text-2xl">{t("auth.login.welcome")}</CardTitle>
              <CardDescription>{t("auth.login.subtitle")}</CardDescription>
            </CardHeader>
            {magicMode ? (
              magicSent ? (
                <>
                  <CardContent className="space-y-4 text-center">
                    <Mail className="h-10 w-10 text-primary mx-auto" />
                    <p className="text-sm text-muted-foreground">
                      If an account exists for <span className="text-foreground">{email}</span>, a one-time sign-in link is on its way. The link expires in 15 minutes.
                    </p>
                  </CardContent>
                  <CardFooter>
                    <button type="button" className="text-sm text-muted-foreground hover:underline mx-auto" onClick={() => { setMagicMode(false); setMagicSent(false); }}>
                      Back to sign in
                    </button>
                  </CardFooter>
                </>
              ) : (
                <form onSubmit={handleSendMagicLink}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="magic-email">{t("auth.email")}</Label>
                      <Input id="magic-email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                      <p className="text-xs text-muted-foreground">We will email you a one-time link to sign in without a password.</p>
                    </div>
                  </CardContent>
                  <CardFooter className="flex-col gap-4">
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      Email me a sign-in link
                    </Button>
                    <button type="button" className="text-sm text-muted-foreground hover:underline" onClick={() => setMagicMode(false)}>
                      Use a password instead
                    </button>
                  </CardFooter>
                </form>
              )
            ) : mfa ? (
              <form onSubmit={handleVerifyMfa}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="mfa-code">Verification code</Label>
                    <p className="text-xs text-muted-foreground">
                      {mfa.method === "email_otp"
                        ? "We sent a 6-digit code to your email. It expires in 10 minutes."
                        : "Enter the code from your authenticator or the one we sent you."}
                    </p>
                    {mfa.stepUpReason === "org_policy" && (
                      <p className="text-xs text-amber-500">
                        Your organization requires two-factor authentication. After signing in, add an authenticator or passkey in Settings, Security.
                      </p>
                    )}
                    {mfa.stepUpReason === "suspicious_login" && (
                      <p className="text-xs text-amber-500">
                        This sign-in looked different from your usual activity, so we added a verification step. If this was not you, change your password after signing in.
                      </p>
                    )}
                    <Input id="mfa-code" inputMode="numeric" aria-label="Verification code" autoComplete="one-time-code" placeholder="123456" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required />
                  </div>
                </CardContent>
                <CardFooter className="flex-col gap-4">
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Verify and sign in
                  </Button>
                  <button type="button" className="text-sm text-muted-foreground hover:underline" onClick={() => { setMfa(null); setMfaCode(""); }}>
                    Back to sign in
                  </button>
                </CardFooter>
              </form>
            ) : (
              <>
                <CardContent className="space-y-4">
                  <OAuthButtons />
                  <div className="relative">
                    <Separator />
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">{t("auth.or")}</span>
                  </div>
                </CardContent>
                <form onSubmit={handleLogin}>
                  <CardContent className="space-y-4 pt-0">
                    <div className="space-y-2">
                      <Label htmlFor="email">{t("auth.email")}</Label>
                      <Input id="email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">{t("auth.password")}</Label>
                        <Link to="/forgot-password" className="text-xs text-primary hover:underline">{t("auth.login.forgot")}</Link>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pr-12"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((visible) => !visible)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          aria-pressed={showPassword}
                          className="absolute right-1 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                        </button>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex-col gap-4">
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t("auth.signIn")}
                    </Button>
                    <button type="button" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" onClick={() => setMagicMode(true)}>
                      <Mail className="h-3.5 w-3.5" /> Email me a sign-in link instead
                    </button>
                    <p className="text-sm text-muted-foreground">
                      {t("auth.login.noAccount")}{" "}
                      <Link to="/signup" className="text-primary hover:underline">{t("auth.login.signUpLink")}</Link>
                    </p>
                  </CardFooter>
                </form>
              </>
            )}
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
